/**
 * Lean short-dashboard content — identity + balance + 4 quick actions.
 *
 * Shared by all three variants (bottom sheet, anchored popover,
 * right-side drawer) so the wrapper component only owns presentation
 * (open animation, dismiss gesture) and this owns the actual rows.
 * Cash App / Robinhood quick-access pattern.
 */
"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  RefreshCw,
} from "lucide-react";
import { api, clearTokens, loadTokens } from "@/lib/api";

interface QuickAction {
  label: string;
  href: string;
  icon: typeof ArrowDownToLine;
}

const QUICK_ACTIONS: QuickAction[] = [
  { label: "Deposit", href: "/deposit", icon: ArrowDownToLine },
  { label: "Withdraw", href: "/withdraw", icon: ArrowUpFromLine },
  { label: "Send", href: "/send", icon: Send },
  { label: "Convert", href: "/convert", icon: RefreshCw },
];

export function QuickDashboardLean({ onClose }: { onClose: () => void }) {
  const router = useRouter();
  const [username, setUsername] = useState<string | null>(null);
  const [balanceUsd, setBalanceUsd] = useState<number | null>(null);

  function handleSignOut() {
    clearTokens();
    setUsername(null);
    onClose();
    router.push("/");
  }

  useEffect(() => {
    const tokens = loadTokens();
    if (!tokens) {
      setUsername(null);
      return;
    }
    setUsername(tokens.user.username);
    (async () => {
      try {
        const wallets = await api.listMyWallets();
        const usdc =
          wallets.find((w) => w.currency === "USDC") ?? wallets[0] ?? null;
        if (usdc) setBalanceUsd(Number(usdc.balance_cents) / 100);
      } catch {
        /* swallow */
      }
    })();
  }, []);

  const initial = username ? username[0]?.toUpperCase() : "?";
  const display = balanceUsd != null ? `$${balanceUsd.toFixed(2)}` : "—";
  // Mock today delta — swap for /v1/me/equity-curve when wired.
  const todayDeltaUsd = 23.4;
  const todayDeltaPct = 3.4;
  const deltaPositive = todayDeltaUsd >= 0;

  return (
    <div className="flex flex-col gap-4 p-5">
      {/* Identity row */}
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-[18px] font-bold"
          style={{
            background: "var(--color-surface2)",
            border: "1px solid var(--color-border)",
            color: "var(--color-text)",
          }}
        >
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold">
            {username ?? "Sign in"}
          </div>
          <div
            className="truncate text-[11px]"
            style={{ color: "var(--color-text3)" }}
          >
            {username ? `@${username}` : "Create an account or log in"}
          </div>
        </div>
        {/* Sign out — right-aligned in the identity row, in line with
         * the avatar + name/handle column (founder direction 2026-05-24).
         * Subtle text link, not a prominent button. */}
        {username && (
          <button
            type="button"
            onClick={handleSignOut}
            className="flex-shrink-0 self-center text-[12px] font-medium underline-offset-2 transition-colors hover:underline"
            style={{ color: "var(--color-text2)" }}
          >
            Sign out
          </button>
        )}
      </div>

      {/* Big balance + today delta + eyebrow (matches the dashboard
       * hero treatment founder showed 2026-05-24) */}
      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span
            className="font-mono text-[32px] font-bold tabular-nums tracking-[-0.02em] sm:text-[36px]"
            style={{ color: "var(--color-text)" }}
          >
            {display}
          </span>
          {balanceUsd != null && (
            <span
              className="inline-flex items-center gap-1 font-mono text-[12px] font-semibold tabular-nums"
              style={{
                color: deltaPositive
                  ? "var(--color-yes)"
                  : "var(--color-no)",
              }}
            >
              {deltaPositive ? (
                <ArrowUpRight size={12} strokeWidth={2.25} />
              ) : (
                <ArrowDownRight size={12} strokeWidth={2.25} />
              )}
              {deltaPositive ? "+" : ""}${Math.abs(todayDeltaUsd).toFixed(2)} ·{" "}
              {todayDeltaPct.toFixed(1)}% today
            </span>
          )}
        </div>
        <div
          className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em]"
          style={{ color: "var(--color-text3)" }}
        >
          Virtual balance · USDC settlement
        </div>
      </div>

      {/* 4 quick actions */}
      <div className="grid grid-cols-4 gap-2">
        {QUICK_ACTIONS.map((a) => (
          <Link
            key={a.label}
            href={a.href}
            onClick={onClose}
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl py-3 transition-colors"
            style={{
              background: "var(--color-surface2)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
            }}
          >
            <span
              aria-hidden="true"
              className="flex h-9 w-9 items-center justify-center rounded-full"
              style={{
                background: "var(--color-accent)",
                color: "#0a0a0a",
              }}
            >
              <a.icon size={16} strokeWidth={2} />
            </span>
            <span className="text-[11px] font-semibold">{a.label}</span>
          </Link>
        ))}
      </div>

      {/* View full dashboard CTA */}
      <Link
        href="/dashboard"
        onClick={onClose}
        className="block rounded-lg py-2.5 text-center text-[13px] font-bold"
        style={{
          background: "var(--color-text)",
          color: "var(--color-bg)",
        }}
      >
        View full dashboard →
      </Link>
      {/* Sign-out is rendered by the wrapping QuickDashboardFullScreen
       * top bar (top-right) per founder direction 2026-05-24, so the
       * Lean content itself doesn't repeat it. handleSignOut/router
       * stay defined for callers that wrap Lean in a chrome WITHOUT a
       * built-in sign-out affordance (sheet/popover/drawer variants). */}
    </div>
  );
}
